/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: History
 * File     : History.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the History view and coordinating data fetching.
 *   Exports: History
 *   
 *
 * Dependencies:
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/icons-material/Delete
 *   - @mui/material
 *   - date-fns
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Use in Router: <Route path="/history" element={<History />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { fetchPolls, updatePoll, deletePoll, buildApiUrl } from '../services/api';
import { Typography, Box, List, ListItem, ListItemText, IconButton, Tooltip, Avatar, Stack, ListItemButton, Chip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

const History = () => {
  const [polls, setPolls] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    fetchPolls({ active: false, limit: 50 }).then(data => setPolls(data.polls || [])).catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!(user?.role === 'owner')) return;
    if (!window.confirm('Delete this poll permanently?')) return;
    try {
      await updatePoll(id, { isActive: false });
      await deletePoll(id);
      setPolls(polls.filter(p => p.id !== id));
    } catch (e) {}
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Past Polls</Typography>
      <List>
        {polls.filter(p => p && p.id).map((p) => (
          <ListItem key={p.id} divider
            secondaryAction={user?.role === 'owner' ? (
              <Tooltip title="Delete poll">
                <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(p.id)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          >
            <ListItemButton component={Link} to={`/results/${p.id}`} sx={{ pr: user?.role === 'owner' ? 9 : 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                {(() => {
                  const pm = p.winner?.movie;
                  if (!pm) return null;
                  const poster = pm.poster;
                  if (!poster || poster === 'N/A') return <Avatar variant="rounded">?</Avatar>;
                  const src = poster.startsWith('http') ? poster : buildApiUrl(`/api/plex/image?path=${encodeURIComponent(poster)}`);
                  return <Avatar variant="rounded" src={src} alt={`${pm.title} poster`} />;
                })()}
                <ListItemText
                  primary={p.title}
                  secondary={
                    p.winner?.movie ? (
                      <span>
                        <a
                          href={`https://www.imdb.com/title/${p.winner.movie.imdbID}/`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          {p.winner.movie.title}
                        </a>{' '}
                        won with {p.winner.voteCount || ''} votes • Ended {formatDistanceToNow(new Date(p.endDate))} ago
                        {p.creator ? <> • Created by <strong>{p.creator.displayName || p.creator.username}</strong></> : null}
                      </span>
                    ) : (
                      <span>
                        Ended {formatDistanceToNow(new Date(p.endDate))} ago
                        {p.creator ? <> • Created by <strong>{p.creator.displayName || p.creator.username}</strong></> : null}
                      </span>
                    )
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
                {p.club && (p.club.hostInactive ? (
                  <Chip color="warning" size="small" label="Host inactive: new polls locked" />
                ) : null)}
              </Stack>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default History;


